<!--suppress JSValidateTypes -->
<script lang="ts">
	import { appState } from '$mapapp/stores/appstate.svelte'
	import Map from './Map.svelte'
	import { setContext, onMount } from 'svelte'
	import { jwt } from '$utils/jwt'
	import RouteAndMatchActionControl from './controls/RouteAndMatchActionControl.svelte'
	import FakControl from './controls/FakControl.svelte'
	import { mapFormControls } from './fak/utils/fakStores'
	import { writable, type Writable } from 'svelte/store'
	import Basic from '../templates/Basic.svelte'
	import { fetchUtils } from '$utils/fetchUtils'
	import PlatformRequestModal from '$lib/map/platformRequests/PlatformRequestModal.svelte'

	const urlParams: URLSearchParams = new URLSearchParams(window.location.search)
	let defaultMinDistance: number = 0
	let defaultMaxDistance: number = 70 // the same value as matching.maxDistance.default from application.conf

	fetchUtils.get('/api/map/defaultDistances').then((data) => {
		defaultMinDistance = data.minDistanceDefault || defaultMinDistance
		defaultMaxDistance = data.maxDistanceDefault || defaultMaxDistance
	})

	let ref: string = urlParams.get('ref') || ''
	let refLot: string = ''
	let startObject: string = ''
	let match: string = urlParams.get('match') || ''
	if (ref) {
		if (ref.startsWith('LOT-')) {
			refLot = ref.substring(4)
		} else if (ref.startsWith('PROJECT-')) {
			startObject = ref
		}
	}
	let formProjectId: string | null = urlParams.get('formProjectId')
	let formLotId: string | null = urlParams.get('formLotId')

	let minDistance: number
	let maxDistance: number

	$: minDistance = Number(urlParams.get('minDistance')) || defaultMinDistance
	$: maxDistance = Number(urlParams.get('maxDistance')) || defaultMaxDistance

	// Block map form for saas
	let allowMapForm = false
	$: if (allowMapForm) {
		if (formProjectId) {
			$mapFormControls.projectId = formProjectId
			if (formLotId) {
				$mapFormControls.lotId = formLotId
			}
			$mapFormControls.level = 1
		}
	}

	let streetViewPermission: boolean = false
	let mapReady: boolean = false
	let jwtReady: boolean = false
	let isOnSaas: boolean = false
	let container: HTMLElement
	let map: Writable<google.maps.Map> = writable()
	let center: google.maps.LatLngLiteral = { lat: 48.8079392, lng: 9.19921149 }

	let platformRequestModal: PlatformRequestModal | undefined
	let platformRequestLotId: string
	let platformRequestPopId: string

	window.initMap = function initMap() {
		$map = new google.maps.Map(container, {
			zoom: 12,
			center: center,
			gestureHandling: 'greedy',
			zoomControlOptions: {
				position: google.maps.ControlPosition.TOP_RIGHT
			},
			mapTypeControlOptions: {
				position: google.maps.ControlPosition.TOP_RIGHT
			},
			streetViewControl: streetViewPermission,
			streetViewControlOptions: {
				position: google.maps.ControlPosition.RIGHT_TOP
			}
			// styles: mapStyles // optional
		})
		mapReady = true
	}

	jwt.subscribe((claims) => {
		if (claims) {
			if (claims.tloc) {
				center = { lat: Number(claims.tloc[0]), lng: Number(claims.tloc[1]) }
			}
			isOnSaas = claims.saas
			allowMapForm = claims.perms && !isOnSaas
			streetViewPermission = claims.perms && claims.perms.includes('permission-street-view')
			jwtReady = !!claims.perms
		}
	})

	setContext('map', map)

	function openPlatformRequest(event: Event) {
		const e = event as CustomEvent<{ lotId: string; popId: string }>
		platformRequestLotId = e.detail.lotId
		platformRequestPopId = e.detail.popId
		platformRequestModal?.openPlatformRequestModal()
	}

	onMount(() => {
		window.addEventListener('openPlatformRequest', openPlatformRequest)

		return () => {
			window.removeEventListener('openPlatformRequest', openPlatformRequest)
		}
	})
</script>

<svelte:head>
	<script src="/assets/js/tippy/popper.js" type="text/javascript"></script>
	<script src="/assets/js/tippy/tippy.js" type="text/javascript"></script>
	<script src="/assets/js/tippy/tippy-initializer.js"></script>
	<script src="/assets/js/platformRequestSiriusButton.js"></script>
	{#if jwtReady}
		<script
			defer
			async
			src="https://maps.googleapis.com/maps/api/js?key=AIzaSyB7sb3Nblnurt744h-KxBWRlLbdzu97Lx0&callback=initMap&libraries=places&v=weekly"
		>
		</script>
	{/if}
</svelte:head>

<svelte:window on:resize={() => (appState.is_desktop = window.innerWidth >= 768)} />

{#if !isOnSaas}
	<PlatformRequestModal
		bind:this={platformRequestModal}
		lotId={platformRequestLotId}
		popId={platformRequestPopId}
	/>
{/if}
<Basic fullscreen>
	<Map bind:container>
		<base target="_parent" />
		{#if mapReady}
			{#if allowMapForm}
				<FakControl position={google.maps.ControlPosition.LEFT_TOP} />
			{/if}
			<RouteAndMatchActionControl
				position={google.maps.ControlPosition.LEFT_TOP}
				{refLot}
				{startObject}
				{match}
				{allowMapForm}
				{minDistance}
				{maxDistance}
			/>
			<!--        TODO uncomment for seeing the usage examples -->
			<!--        <Marker></Marker>-->
			<!--        <Control position={google.maps.ControlPosition.LEFT_TOP}></Control>-->
		{/if}
	</Map>
</Basic>

<style>
	:global(body) {
		padding: 0;
	}
</style>
